<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import ContactAPI from 'dashboard/api/contacts';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const I18N_BASE = 'CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_HISTORY';
const { t } = useI18n();

const dialogRef = ref(null);
const imports = ref([]);
const isLoading = ref(false);
const undoingId = ref(null);
const confirmUndoId = ref(null);

const formatDate = dateStr => {
  const date = new Date(dateStr);
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const STATUS_CLASSES = {
  completed: 'text-n-teal-11 bg-n-teal-3',
  processing: 'text-n-amber-11 bg-n-amber-3',
  failed: 'text-n-ruby-11 bg-n-ruby-3',
};

const statusClass = status =>
  STATUS_CLASSES[status] || 'text-n-slate-11 bg-n-alpha-2';

const getStatusLabel = status => {
  const keys = {
    completed: `${I18N_BASE}.STATUS_LABELS.completed`,
    processing: `${I18N_BASE}.STATUS_LABELS.processing`,
    failed: `${I18N_BASE}.STATUS_LABELS.failed`,
  };
  return keys[status] ? t(keys[status]) : status;
};

const sortedImports = computed(() =>
  [...imports.value].sort(
    (a, b) => new Date(b.created_at) - new Date(a.created_at)
  )
);

const fetchHistory = async () => {
  isLoading.value = true;
  try {
    const response = await ContactAPI.getImportHistory();
    imports.value = response.data.data || []; // The response comes from the server in `data` key
  } catch {
    imports.value = [];
  } finally {
    isLoading.value = false;
  }
};

const handleUndoClick = importRecord => {
  confirmUndoId.value = importRecord.id;
};

const cancelUndo = () => {
  confirmUndoId.value = null;
};

const confirmUndo = async importRecord => {
  undoingId.value = importRecord.id;
  confirmUndoId.value = null;
  try {
    const response = await ContactAPI.undoImport(importRecord.id);
    const { deleted_count: deleted, protected_count: protectedCount } =
      response.data;
    useAlert(
      t(`${I18N_BASE}.UNDO_SUCCESS`, {
        deleted,
        protected: protectedCount,
      })
    );
    await fetchHistory();
  } catch {
    useAlert(t(`${I18N_BASE}.UNDO_ERROR`));
  } finally {
    undoingId.value = null;
  }
};

onMounted(fetchHistory);

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t(`${I18N_BASE}.TITLE`)"
    :show-confirm-button="false"
  >
    <div v-if="isLoading" class="flex justify-center py-8">
      <span class="i-lucide-loader-2 animate-spin text-n-slate-11 size-6" />
    </div>

    <div
      v-else-if="sortedImports.length === 0"
      class="flex flex-col items-center justify-center py-8 text-n-slate-11"
    >
      <span class="i-lucide-inbox size-10 mb-2" />
      <p class="text-sm">
        {{ t(`${I18N_BASE}.EMPTY_STATE`) }}
      </p>
    </div>

    <div v-else class="flex flex-col gap-3 max-h-[400px] overflow-y-auto">
      <div
        v-for="record in sortedImports"
        :key="record.id"
        class="rounded-lg border border-n-weak p-3 flex flex-col gap-2"
      >
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2">
            <span class="text-sm font-medium text-n-slate-12">
              {{ record.original_filename || 'contacts.csv' }}
            </span>
            <span
              :class="statusClass(record.status)"
              class="text-xs px-2 py-0.5 rounded-full font-medium"
            >
              {{ getStatusLabel(record.status) }}
            </span>
          </div>
          <span class="text-xs text-n-slate-11">
            {{ formatDate(record.created_at) }}
          </span>
        </div>

        <div class="flex items-center gap-4 text-xs text-n-slate-11">
          <span>
            {{ t(`${I18N_BASE}.TOTAL`) }}{{ ': ' }}
            <strong class="text-n-slate-12">
              {{ record.total_records || 0 }}
            </strong>
          </span>
          <span>
            {{ t(`${I18N_BASE}.CREATED`) }}{{ ': ' }}
            <strong class="text-n-teal-11">
              {{ record.created_count || 0 }}
            </strong>
          </span>
          <span>
            {{ t(`${I18N_BASE}.UPDATED`) }}{{ ': ' }}
            <strong class="text-n-blue-11">
              {{ record.updated_count || 0 }}
            </strong>
          </span>
          <span>
            {{ t(`${I18N_BASE}.FAILED`) }}{{ ': ' }}
            <strong class="text-n-ruby-11">
              {{ record.failed_count || 0 }}
            </strong>
          </span>
        </div>

        <div
          v-if="record.status === 'completed' && record.undoable_count > 0"
          class="flex items-center justify-end"
        >
          <div
            v-if="confirmUndoId === record.id"
            class="flex items-center gap-2"
          >
            <span class="text-xs text-n-slate-11">
              {{
                t(`${I18N_BASE}.UNDO_CONFIRM.DESCRIPTION`, {
                  count: record.undoable_count,
                  protected: record.protected_count || 0,
                })
              }}
            </span>
            <Button
              :label="t(`${I18N_BASE}.UNDO_CONFIRM.CONFIRM`)"
              color="ruby"
              size="xs"
              :is-loading="undoingId === record.id"
              @click="confirmUndo(record)"
            />
            <Button
              :label="t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.CANCEL')"
              color="slate"
              variant="ghost"
              size="xs"
              @click="cancelUndo"
            />
          </div>
          <Button
            v-else
            :label="t(`${I18N_BASE}.UNDO`)"
            icon="i-lucide-undo-2"
            color="slate"
            variant="ghost"
            size="xs"
            :is-loading="undoingId === record.id"
            @click="handleUndoClick(record)"
          />
        </div>
      </div>
    </div>
  </Dialog>
</template>
